/**
 * @file useScheduleOverride.js
 *
 * Schedule Override Hook
 *
 * Manages per-date exceptions to the weekly schedule.
 * Pattern: fetch on mount, optimistic delete, re-fetch after create.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { scheduleOverrideService } from "../services";
import { SCHEDULE_OVERRIDE_STRINGS } from "../constants/strings";

/**
 * Hook for managing per-date schedule exceptions (blocked days, special hours).
 *
 * @returns {{overrides: Array, isLoading: boolean, error: Error|null, refresh: Function, createOverride: Function, updateOverride: Function, deleteOverride: Function}}
 */
export function useScheduleOverride() {
  const [overrides, setOverrides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all overrides for the logged-in professional
  const fetchOverrides = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await scheduleOverrideService.getMe();
      setOverrides(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create a new override, then re-fetch to stay in sync
  const createOverride = async (data) => {
    const response = await scheduleOverrideService.create(data);
    await fetchOverrides();
    toast.success(SCHEDULE_OVERRIDE_STRINGS.CREATED);
    return response.data;
  };

  // Update an override
  const updateOverride = async (id, data) => {
    const response = await scheduleOverrideService.update(id, data);
    await fetchOverrides();
    toast.success(SCHEDULE_OVERRIDE_STRINGS.UPDATED);
    return response.data;
  };

  // Optimistic delete: remove from local state immediately
  const deleteOverride = async (id) => {
    await scheduleOverrideService.remove(id);
    setOverrides((prev) => prev.filter((o) => o.id !== id));
    toast.success(SCHEDULE_OVERRIDE_STRINGS.DELETED);
  };

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  return {
    overrides,
    isLoading,
    error,
    refresh: fetchOverrides,
    createOverride,
    updateOverride,
    deleteOverride,
  };
}
